import React, { useContext, useEffect, useState } from 'react';
import { Sidebar } from 'primereact/sidebar';
import SvgRingilAvatar from '@icons/ringil-avatar.svg?react';
import CloseButton from '@components/RingilComponents/CloseButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useAssistantChat from '@hooks/useAssistantChat';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import AssistantIntro from './AssistantIntro';
import AssistantMessageList from './AssistantMessageList';
import AssistantComposer from './AssistantComposer';
import { initialsFromName } from './assistantDate';
import { sidebarTransitionOptions } from '@components/ringil3/Sidebar/sidebarTransition.ts';
import usePanelDock from '@components/panelDock/usePanelDock.ts';
import PanelDockControls from '@components/panelDock/PanelDockControls.tsx';
import MinimizedPanelPill from '@components/panelDock/MinimizedPanelPill.tsx';
import FloatingPanel from '@components/panelDock/FloatingPanel.tsx';
import PanelDragGrip from '@components/panelDock/PanelDragGrip.tsx';
import SvgAiAssistant from '@icons/ai-robot.svg?react';

import { ASSISTANT_PANEL_KEY } from './assistantContext.ts';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
}

/**
 * The AI assistant conversation panel (PoC).
 *
 * Uses PrimeReact's Sidebar directly rather than RingilSidebar: this panel needs a fixed header, an
 * independently scrolling message area and a pinned composer, whereas RingilSidebar is built around a
 * single scrolling form body with Save/Back buttons.
 *
 * Dockable (components/panelDock): either edge, minimizable, and floatable as a window — the header is
 * the drag handle. The content renders in whichever container the dock says; the conversation and the
 * draft live here, above both, so switching containers loses neither.
 */
const AssistantSidebar: React.FC<ComponentProps> = ({ visible, onHide }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  // Non-modal: the assistant describes the page, so the page has to stay usable while it is open.
  const dock = usePanelDock(ASSISTANT_PANEL_KEY, { visible, onHide, nonModal: true, blockScroll: true, floatable: true });
  const [draft, setDraft] = useState('');
  const {
    messages,
    hasConversation,
    hasMoreHistory,
    loadingHistory,
    sending,
    openConversation,
    closeConversation,
    loadOlderMessages,
    sendMessage,
    cancelPendingRequest,
    startNewConversation,
  } = useAssistantChat();

  // Resume (or reset to) the stored conversation every time the panel is opened, and let go of the
  // session stream when it closes. Opening is the only trigger — re-running when openConversation's
  // identity changes would wipe the live conversation.
  useEffect(() => {
    if (visible) openConversation();
    else closeConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const userName = [loggedUser?.firstName, loggedUser?.lastName].filter(Boolean).join(' ');
  const userInitials = initialsFromName(loggedUser?.firstName, loggedUser?.lastName);

  const content = (
    <>
      {/* Figma 21746:18246: the panel title on the left, the dock actions and close cross on the right. */}
      <div {...dock.dragHandleProps} className={`flex shrink-0 items-center justify-between gap-4 px-6 pt-6 ${dock.dragHandleProps.className}`}>
        <PanelDragGrip dock={dock} />
        <div className="min-w-0 flex-1 truncate text-base font-semibold tracking-[-0.16px] text-content-primary">{tr('Assistant.panelTitle', 'Potřebuji poradit')}</div>
        <div className="flex items-center gap-2">
          <PanelDockControls dock={dock} />
          <CloseButton onClick={onHide} />
        </div>
      </div>

      {hasConversation && (
        <div className="mx-6 my-6 flex shrink-0 items-center gap-2 bg-white px-4 py-2 shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)]">
          <SvgRingilAvatar className="h-[21px] w-[21px] shrink-0" />
          <div className="min-w-0 flex-1 text-sm text-content-primary">
            {tr('Assistant.answeredBy', 'Odpovídá:')} <span className="font-bold">{tr('Assistant.assistantName', 'Ringil Asistent')}</span>
          </div>
          <button
            type="button"
            onClick={startNewConversation}
            className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-right text-xs tracking-[0.24px] text-content-brand transition-all duration-200 hover:text-complementary-brandDark"
          >
            {tr('Assistant.startOver', 'Začít znovu')}
          </button>
        </div>
      )}

      {hasConversation ? (
        <AssistantMessageList
          messages={messages}
          hasMoreHistory={hasMoreHistory}
          loadingHistory={loadingHistory}
          userName={userName}
          userInitials={userInitials}
          onLoadOlder={loadOlderMessages}
          onRetry={sendMessage}
          onPickSuggestion={sendMessage}
        />
      ) : (
        <AssistantIntro />
      )}
      {/* Pinned in both states: asking the first question is what the empty screen is for. */}
      <AssistantComposer text={draft} onTextChange={setDraft} sending={sending} onSend={sendMessage} onCancel={cancelPendingRequest} />
    </>
  );

  return (
    <>
      {/* @ts-expect-error primereact's Sidebar props type omits `children` (missing React 18 augmentation) */}
      <Sidebar
        transitionOptions={sidebarTransitionOptions}
        {...dock.sidebarProps}
        showCloseIcon={false}
        closeOnEscape={true}
        dismissable={true}
        style={{ maxWidth: '550px', width: '100%' }}
        // The panel owns its own header/scroll/footer, so PrimeReact's padded content box is flattened.
        className={`${dock.className} !overflow-hidden bg-base-base [&_.p-sidebar-content]:!flex [&_.p-sidebar-content]:!flex-col [&_.p-sidebar-content]:!overflow-hidden [&_.p-sidebar-content]:!p-0 [&_.p-sidebar-header]:!hidden`}
      >
        {content}
      </Sidebar>
      <FloatingPanel dock={dock} className="bg-base-base">
        {content}
      </FloatingPanel>
      <MinimizedPanelPill dock={dock} icon={<SvgAiAssistant className="h-4 w-4" />} label={tr('Assistant.assistantName', 'Ringil Assistant')} />
    </>
  );
};

export default AssistantSidebar;
